import React, { useState } from 'react';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { saveAppointment } from '../services/api';

const APPOINTMENT_TYPES = [
  'Pastor Appointment',
  'Saturday Teaching',
];

const AppointmentForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    appointmentType: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
  });

  const [validated, setValidated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 3) {
        newValue = digits;
      } else if (digits.length <= 6) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await saveAppointment(formData);
      setSubmitted(true);
      setValidated(false);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        appointmentType: '',
        preferredDate: '',
        preferredTime: '',
        message: '',
      });
    } catch (err) {
      console.error('Error submitting appointment:', err);
      setError(err.message || 'Failed to submit appointment. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Minimum date = today
  const today = new Date().toISOString().split('T')[0];

  return (
    <section className="py-3" id="appointment">
      <div className="card shadow">
        <div className="card-body p-4">
          <h2 className="text-center fw-bold mb-2">Request an Appointment</h2>
          <p className="text-center text-muted mb-4">
            Fill out the form below and we'll confirm your appointment by email.
          </p>

          {submitted && (
            <Alert variant="success" className="mb-4">
              <Alert.Heading>Request Received!</Alert.Heading>
              <p>
                Thank you for reaching out. We'll confirm your appointment by email as soon as
                possible.
              </p>
              <div className="text-center mt-3">
                <Button as={Link} to="/" variant="outline-success">
                  Return to Home
                </Button>
              </div>
            </Alert>
          )}

          {error && !submitted && (
            <Alert variant="danger" className="mb-4">
              <Alert.Heading>Submission Error</Alert.Heading>
              <p>{error}</p>
            </Alert>
          )}

          {!submitted && (
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      First name is required.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      Last name is required.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      required
                      type="tel"
                      name="phone"
                      pattern="\d{3}-\d{3}-\d{4}"
                      placeholder="123-456-7890"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength={12}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid phone number (123-456-7890).
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid email address.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Type of Appointment</Form.Label>
                <Form.Select
                  required
                  name="appointmentType"
                  value={formData.appointmentType}
                  onChange={handleChange}
                >
                  <option value="">Select an appointment type...</option>
                  {APPOINTMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Please select an appointment type.
                </Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Preferred Date</Form.Label>
                    <Form.Control
                      required
                      type="date"
                      name="preferredDate"
                      min={today}
                      value={formData.preferredDate}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please select a preferred date.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Preferred Time</Form.Label>
                    <Form.Select
                      required
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                    >
                      <option value="">Select a time...</option>
                      <option value="Morning (9am–12pm)">Morning (9am–12pm)</option>
                      <option value="Afternoon (12pm–3pm)">Afternoon (12pm–3pm)</option>
                      <option value="Late Afternoon (3pm–6pm)">Late Afternoon (3pm–6pm)</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select a preferred time.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Label>
                  Additional Message <span className="text-muted">(optional)</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Share anything that would help us prepare for your appointment..."
                />
              </Form.Group>

              <div className="d-grid gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="fw-bold"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Request Appointment'}
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </section>
  );
};

export default AppointmentForm;
